import { Fingerprint } from "lucide-react";

import type { FilterDefinition } from "@/shared/foundation/primitives/Table";

/**
 * Comparators for resource-id flavored string fields — opaque identifiers
 * the backend matches exactly. `SessionPredicateGenerator` rejects anything
 * outside `{EQUALS, NOT_EQUALS}` on `id`, `dataset.dataset_id`, and
 * `device.device_id`, so the picker mirrors that allow-list.
 */
const RESOURCE_ID_COMPARATORS = ["EQUALS", "NOT_EQUALS"] as const;

/** Fingerprint glyph shared by every resource-id flavored Sessions filter row. */
const RESOURCE_ID_ICON = <Fingerprint />;

/**
 * Comparator + icon preset that mounts apply to the cross-aggregate id
 * rows (`dataset.dataset_id`, `device.device_id`) they append, so those
 * rows match the core `Session ID` row's operator set and glyph.
 */
export const sessionResourceIdFilter = {
  comparators: RESOURCE_ID_COMPARATORS,
  icon: RESOURCE_ID_ICON,
} as const;

/**
 * Filterable Sessions fields common to every Sessions list mount. Mounts
 * compose their picker by spreading this array and appending the
 * cross-aggregate id rows their scope admits. Each `field` is the roboQL
 * token (alias or canonical label) the backend route forwards to
 * `SessionPredicateGenerator`.
 */
export const coreSessionFilters = [
  {
    type: "string",
    field: "id",
    name: "Session ID",
    allowedComparators: RESOURCE_ID_COMPARATORS,
    icon: RESOURCE_ID_ICON,
  },
  { type: "string", field: "name", name: "Name" },
  { type: "date", field: "start_time", name: "Start time" },
  { type: "date", field: "end_time", name: "End time" },
  { type: "numeric", field: "duration", name: "Duration (ns)" },
] as const satisfies readonly FilterDefinition[];
